import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import LoadingIcon from "components/Loading/LoadingIcon.tsx";
import { useToken } from "utils/context.ts";
import React from "react";

type ProtectedRouteProps = {
  children?: React.ReactNode;
  isAllowed?: boolean;
  redirectPath?: string;
};

/**
 * A wrapper component that redirects to the login page if the user is not authenticated.
 * Can be used either as a wrapper component with children or as a layout route with Outlet.
 */
function ProtectedRoute({
  children,
  isAllowed,
  redirectPath = "/login",
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth0();
  const { accessToken } = useToken();
  const location = useLocation();
  const navigate = useNavigate();

  // if isAllowed is provided, use it; otherwise default to isAuthenticated
  const allowed = isAllowed !== undefined ? isAllowed : isAuthenticated;

  // Hold rendering until the token is in Redux; otherwise child queries fire
  // unauthenticated and trigger the 401 force-logout path.
  const waitingForToken = isAuthenticated && !accessToken;

  useEffect(() => {
    if (!isLoading && !allowed) {
      localStorage.setItem("returnTo", location.pathname + location.search);
    }
  }, [allowed, isLoading, location]);

  // redirect when authentication changes
  useEffect(() => {
    if (!isLoading && !allowed) {
      navigate(redirectPath);
    }
  }, [isLoading, allowed, navigate, redirectPath]);

  // handle trailing slashes
  useEffect(() => {
    if (location.pathname.endsWith("/") && location.pathname !== "/") {
      const newPath = location.pathname.slice(0, -1);
      navigate(newPath, { replace: true });
    }
  }, [location.pathname, navigate]);

  if (isLoading || waitingForToken) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <LoadingIcon />
      </div>
    );
  }

  // render children if provided, otherwise use Outlet for nested routes
  return allowed ? children || <Outlet /> : null;
}

export default ProtectedRoute;
